import * as React from 'react';
import {
  Typography,
  Box,
  Container,
  Button,
  Grid,
  CircularProgress,
  Stack,
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { centered_flex_box } from '../../app/components/Styles';
import { EventCard } from "../../app/components";
import Inbox from '@mui/icons-material/Inbox';
import moment from 'moment';
import { getFeaturedEvents } from '../../app/store/actions/eventActions';
import TickitLogo from '../../app/components/TickitLogo';
import ReactGA from 'react-ga4';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { MarketingButton } from '../../app/components/marketing';
import {
  marketingColors,
  marketingFonts,
  marketingPageShellSx,
  sectionLabelSx,
  sectionTitleSx,
  featureCardSx,
  eventSurfacePaperSx,
} from '../../app/components/tickitMarketingTheme';

const steps = [
  { n: '01', title: 'Publish your event', body: 'Set capacity, ticket types, gates, and visibility — private galas or public listings.' },
  { n: '02', title: 'Send invitations', body: 'Share RSVP and ticket links with your guest list; we support structured invitation flows.' },
  { n: '03', title: 'Track sales & requests', body: 'Monitor orders, ticket requests, and attendance from your organizer tools.' },
  { n: '04', title: 'Check in at the door', body: 'Validate QR tickets with the usher scanner for a smooth arrival experience.' },
];

export const Home = ({ events, getFeaturedEvents, token }) => {

    const navigate = useNavigate()
    const role = token?.split(" ")[0]

    React.useEffect(() => {
        getFeaturedEvents({})
        ReactGA.send({ hitType: "pageview", page: "/", title: "Home" });
    }, [])

    return (
        <Box component="main" id="main-content" sx={{ ...marketingPageShellSx, width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    minHeight: { xs: 'auto', md: 'calc(100vh - 60px)' },
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ flex: { xs: 'none', md: '0 0 auto' }, pt: { xs: 3, md: 2 }, pb: { xs: 4, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 3, md: 2.5 }} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography
                                sx={{
                                    fontFamily: marketingFonts.display,
                                    fontWeight: 800,
                                    fontSize: { xs: '2rem', sm: '2.5rem', md: 'clamp(1.85rem, 2.5vw + 1rem, 2.65rem)' },
                                    lineHeight: 1.08,
                                    letterSpacing: '-0.04em',
                                    color: marketingColors.gray900,
                                    mb: { xs: 1.5, md: 1 },
                                }}
                            >
                                Ticketing & invitations{' '}
                                <Box component="span" sx={{ color: marketingColors.p700, position: 'relative' }}>
                                    without the chaos
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            height: 4,
                                            borderRadius: 1,
                                            mt: 0.5,
                                            maxWidth: 280,
                                            background: `linear-gradient(90deg, ${marketingColors.p400}, ${marketingColors.p300})`,
                                            opacity: 0.55,
                                        }}
                                    />
                                </Box>
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: marketingFonts.body,
                                    fontSize: { xs: '0.95rem', md: '0.9rem' },
                                    color: marketingColors.gray500,
                                    lineHeight: 1.55,
                                    maxWidth: 520,
                                    mb: { xs: 2, md: 1.5 },
                                }}
                            >
                                Sell tickets, manage invitation responses, and run check-in — one platform for weddings, corporate nights, and ticketed experiences across MENA.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <MarketingButton
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate('/events/request')}
                                    sx={{ py: 1.25, px: 2.5 }}
                                >
                                    Get powered by Tickit
                                </MarketingButton>
                                <MarketingButton
                                    variant="outline"
                                    onClick={() => navigate('/features')}
                                    sx={{ py: 1.25, px: 2.5 }}
                                >
                                    See features
                                </MarketingButton>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    ...eventSurfacePaperSx,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: { xs: 'none', md: 'block' },
                                        background: `linear-gradient(135deg, ${marketingColors.p800} 0%, ${marketingColors.p600} 60%, ${marketingColors.p500} 100%)`,
                                        p: 2,
                                        color: '#fff',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.92)', textTransform: 'uppercase', mb: 0.5, fontFamily: marketingFonts.body }}>
                                        Featured on Tickit
                                    </Typography>
                                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', lineHeight: 1.25, textShadow: '0 1px 2px rgba(30, 5, 80, 0.35)' }}>
                                        Your next event, professionally managed
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        p: { xs: 2, md: 1.5 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: { xs: 160, md: 120 },
                                        background: '#ffffff',
                                    }}
                                >
                                    <TickitLogo variant="onLight" height={72} />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* How it works — fills remaining first-screen height; content vertically centered on desktop */}
            <Box sx={{
                flex: { xs: 'none', md: '1 1 auto' },
                minHeight: { xs: 'auto', md: 0 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'center' },
                pb: { xs: 5, md: 6 },
                px: { xs: 2, md: 4 },
                background: marketingColors.surface2,
                borderTop: `1px solid ${marketingColors.border}`,
            }}>
                <Container maxWidth="lg" sx={{ py: 0, width: '100%' }}>
                    <Typography sx={{ ...sectionLabelSx, mb: { xs: 1, md: 0.5 } }}>How it works</Typography>
                    <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '1.5rem', md: '1.35rem' }, mb: { xs: 0.75, md: 0.5 } }}>
                        From invite to door scan
                    </Typography>
                    <Typography sx={{ fontFamily: marketingFonts.body, color: marketingColors.gray500, maxWidth: 560, mb: { xs: 2, md: 1.5 }, lineHeight: 1.5, fontSize: { xs: '0.9rem', md: '0.82rem' } }}>
                        A straightforward flow your team and guests can follow — built on the tools we ship today.
                    </Typography>
                    <Grid container spacing={{ xs: 2, md: 1.5 }}>
                        {steps.map((s) => (
                            <Grid item xs={12} sm={6} md={3} key={s.n}>
                                <Box sx={{
                                    ...featureCardSx,
                                    p: { xs: 2.5, md: 1.75 },
                                    minHeight: { xs: 160, md: 0 },
                                }}>
                                    <Typography sx={{ fontFamily: marketingFonts.display, fontSize: { xs: '2rem', md: '1.75rem' }, fontWeight: 800, color: marketingColors.p100, lineHeight: 1, mb: { xs: 1, md: 0.75 } }}>
                                        {s.n}
                                    </Typography>
                                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: { xs: '0.95rem', md: '0.85rem' }, color: marketingColors.gray900, mb: 0.5 }}>
                                        {s.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: marketingFonts.body, fontSize: { xs: '0.8rem', md: '0.72rem' }, color: marketingColors.gray500, lineHeight: 1.45 }}>
                                        {s.body}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            </Box>

            {/* Featured Events Section */}
            {(!role || role !== "Partner") && (
                <Container maxWidth="xl" sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 5, md: 7 }, px: { xs: 2, md: 4 } }}>
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Typography sx={sectionLabelSx}>Discover</Typography>
                        <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                            Featured events
                        </Typography>
                        <Box sx={{ width: 72, height: 4, background: `linear-gradient(90deg, ${marketingColors.p700}, ${marketingColors.p400})`, borderRadius: 1, mx: 'auto', mt: 2 }} />
                    </Box>

                    {events?.results && Array.isArray(events.results) && !events.isLoading ? (
                        <Grid container spacing={1.75}>
                            {events?.results?.filter((event) => moment().isBefore(event.date)).length > 0 &&
                                events?.results?.filter((event) => moment().isBefore(event.date)).map((event) => (
                                    <Grid item xs={12} sm={6} md={4} key={event._id}>
                                        <EventCard event={event} />
                                    </Grid>
                                ))}
                            {events?.results?.filter((event) => moment().isBefore(event.date)).length === 0 && (
                                <Grid item xs={12} sx={{ textAlign: 'center', py: 8 }}>
                                    <Inbox sx={{ fontSize: 64, color: marketingColors.gray400, mb: 2 }} />
                                    <Typography variant="h5" sx={{ color: marketingColors.gray600, fontFamily: marketingFonts.display, fontWeight: 800 }}>
                                        No upcoming events
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: marketingColors.p700 }} />
                        </Box>
                    )}
                </Container>
            )}

            {/* Past Events Section */}
            {(!role || role !== "Partner") && (
                <Container maxWidth="xl" sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 5, md: 7 }, px: { xs: 2, md: 4 }, borderTop: `1px solid ${marketingColors.border}` }}>
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Typography sx={sectionLabelSx}>Archive</Typography>
                        <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                            Past events
                        </Typography>
                        <Box sx={{ width: 72, height: 4, background: `linear-gradient(90deg, ${marketingColors.p700}, ${marketingColors.p400})`, borderRadius: 1, mx: 'auto', mt: 2 }} />
                    </Box>

                    {events?.results && Array.isArray(events.results) && !events.isLoading ? (
                        <Grid container spacing={1.75}>
                            {events?.results?.filter((event) => moment().isAfter(event.date)).length > 0 &&
                                events?.results?.filter((event) => moment().isAfter(event.date)).map((event) => (
                                    <Grid item xs={12} sm={6} md={4} key={event._id}>
                                        <EventCard event={event} />
                                    </Grid>
                                ))}
                            {events?.results?.filter((event) => moment().isAfter(event.date)).length === 0 && (
                                <Grid item xs={12} sx={{ textAlign: 'center', py: 8 }}>
                                    <Inbox sx={{ fontSize: 64, color: marketingColors.gray400, mb: 2 }} />
                                    <Typography variant="h5" sx={{ color: marketingColors.gray600, fontFamily: marketingFonts.display, fontWeight: 800 }}>
                                        No past events
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: marketingColors.p700 }} />
                        </Box>
                    )}
                </Container>
            )}

            {/* CTA */}
            <Box
                sx={{
                    pt: { xs: 13, md: 18 },
                    pb: { xs: 8, md: 10 },
                    px: 2,
                    textAlign: 'center',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${marketingColors.p900} 0%, ${marketingColors.p700} 55%, ${marketingColors.p500} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 480,
                        height: 480,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)',
                    },
                }}
            >
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.35rem' }, color: '#fff', letterSpacing: '-0.03em', mb: 2 }}>
                        Ready to power your next event?
                    </Typography>
                    <Typography sx={{ fontFamily: marketingFonts.body, color: 'rgba(255,255,255,0.72)', mb: 3, lineHeight: 1.75, maxWidth: 480, mx: 'auto' }}>
                        Tell us about your wedding, corporate night, or ticketed show — we will follow up with onboarding steps.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/events/request')}
                        sx={{
                            backgroundColor: '#fff',
                            color: marketingColors.p700,
                            fontFamily: marketingFonts.display,
                            fontWeight: 800,
                            textTransform: 'none',
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            '&:hover': { backgroundColor: marketingColors.p100 },
                        }}
                    >
                        Start with Tickit
                    </Button>
                </Container>
            </Box>
        </Box>
    );
}

const mapStateToProps = (state) => ({
    events: state?.events,
    token: state?.auth?.token
});

const mapDispatchToProps = { getFeaturedEvents };

export default connect(mapStateToProps, mapDispatchToProps)(Home);
